import { useEffect, useRef, useState } from 'react';
import { Icon } from './ui';
import { authFetch } from '../hooks/useApi';
import { findAvailableDay } from '../utils/dateNavigation';
import { amsterdamDay } from '../utils/activity';

// Kalender en pijlen zoeken met dezelfde gegevensfilters als de actieve view.
export default function DayPicker({ selectedDay, onChange, hasData, scopeKey = '', transponder = null, maxDay = amsterdamDay() }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const controller = useRef(null);
  useEffect(() => {
    controller.current?.abort();
    setBusy(false);
    setMessage(null);
    return () => controller.current?.abort();
  }, [scopeKey, selectedDay, transponder, maxDay]);

  async function move(direction, target = null) {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setBusy(true);
    setMessage(null);
    try {
      const getDays = async (anchor, step) => {
        const params = new URLSearchParams({ [step < 0 ? 'before' : 'after']: anchor });
        if (transponder) params.set('transponder', transponder);
        const response = await authFetch(`/api/days?${params}`, { signal: request.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const available = await response.json();
        return available.days.filter(day => day <= maxDay);
      };
      const next = await findAvailableDay(getDays, selectedDay, direction, hasData ?? (async () => true), request.signal, target);
      request.signal.throwIfAborted();
      if (next) onChange(next);
      else setMessage(`Geen ${direction < 0 ? 'eerdere' : 'latere'} datum met gegevens voor deze filters.`);
    } catch (error) {
      if (!request.signal.aborted) setMessage(`Datum zoeken mislukt: ${error.message}`);
    } finally {
      if (!request.signal.aborted) setBusy(false);
    }
  }

  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1" aria-busy={busy}>
      <button type="button" className="h-12 w-10 rounded-md border border-line bg-surface text-ink-2 disabled:opacity-40"
        aria-label="Vorige dag met gegevens" disabled={busy || !selectedDay} onClick={() => move(-1)}>
        <Icon name="chevronLeft" size={18} />
      </button>
    <span className="relative inline-flex shrink-0 rounded-md focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary-500">
      <span
        className="pointer-events-none flex h-12 w-12 items-center justify-center rounded-md border border-line bg-surface text-ink-2"
        aria-hidden="true"
      >
        <Icon name="calendar" size={18} />
      </span>
      <input
        type="date"
        value={selectedDay ?? ''}
        max={maxDay}
        disabled={busy}
        aria-label="Kies een datum"
        onChange={e => e.target.value && move(e.target.value < selectedDay ? -1 : 1, e.target.value)}
        // Chrome opent de native kalender alleen bij een klik op het icoontje.
        // showPicker() laat een klik (of focus) op het hele veld hem openen.
        onClick={e => { try { e.currentTarget.showPicker?.(); } catch { /* niet ondersteund */ } }}
        onFocus={e => { try { e.currentTarget.showPicker?.(); } catch { /* niet ondersteund */ } }}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 focus-visible:opacity-0"
      />
    </span>
      <button type="button" className="h-12 w-10 rounded-md border border-line bg-surface text-ink-2 disabled:opacity-40"
        aria-label="Volgende dag met gegevens" disabled={busy || !selectedDay || selectedDay >= maxDay} onClick={() => move(1)}>
        <Icon name="chevronRight" size={18} />
      </button>
      {(busy || message) && <span role="status" className="basis-full text-xs text-ink-3">{busy ? 'Datum met gegevens zoeken…' : message}</span>}
    </span>
  );
}
